import { Tooltip } from '@components/core-ui/Tooltip';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { TemporalPendingActivityDto, TemporalWorkflowStatusDto } from 'app/API';
import { getTemporalConfig } from 'app/http/temporalConfig';
import { useEffect, useMemo, useState } from 'react';

import { formatWorkflowType, HEARTBEAT_STALE_MS, heartbeatAgeMs } from '../helpers/pipelineDisplay';
import { PipelineStatusTag } from '../helpers/statusBadgeTag';

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '---';
  }
  return getAmericanTime(value, true, true);
};

type TemporalTarget = { baseUrl: string; namespace: string };

const TEMPORAL_TARGETS_BY_HOST: Record<string, TemporalTarget> = {
  'autoscript.ai': { baseUrl: 'https://temporal.autoscript.ai', namespace: 'production' },
  'www.autoscript.ai': { baseUrl: 'https://temporal.autoscript.ai', namespace: 'production' },
  'staging.autoscript.ai': { baseUrl: 'https://temporal-staging.autoscript.ai', namespace: 'staging' },
  'demo.autoscript.ai': { baseUrl: 'https://temporal-staging.autoscript.ai', namespace: 'demo' },
};

const resolveFallbackTarget = (): TemporalTarget =>
  TEMPORAL_TARGETS_BY_HOST[window.location.hostname] ?? {
    baseUrl: import.meta.env.VITE_TEMPORAL_UI_URL || 'https://temporal-staging.autoscript.ai',
    namespace: import.meta.env.VITE_TEMPORAL_NAMESPACE || 'staging',
  };

const getTemporalDebugUrl = (workflow: TemporalWorkflowStatusDto, target: TemporalTarget) => {
  const trimmedBaseUrl = target.baseUrl.replace(/\/$/, '');

  return `${trimmedBaseUrl}/namespaces/${encodeURIComponent(target.namespace)}/workflows/${encodeURIComponent(
    workflow.workflowId,
  )}/${encodeURIComponent(workflow.runId)}/history`;
};

const PendingActivityRow = ({
  activity,
  debugUrl,
  workflowRunning,
}: {
  activity: TemporalPendingActivityDto;
  debugUrl: string;
  workflowRunning: boolean;
}) => {
  const isRetrying = typeof activity.attempt === 'number' && activity.attempt > 1;
  const isDeliveringResult = activity.activityType === 'postPipelineEvent' && isRetrying;
  const age = workflowRunning ? heartbeatAgeMs(activity.lastHeartbeatAt) : null;
  const heartbeatStale = age !== null && age > HEARTBEAT_STALE_MS;

  return (
    <li className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-text-subtle)]">
      <span className="font-medium text-[var(--color-text)]">{activity.activityType ?? 'Unknown activity'}</span>
      {activity.state ? (
        <span className="rounded-full bg-[var(--color-surface-muted)] px-2 py-0.5 text-xs font-semibold uppercase text-[var(--color-text-muted)]">
          {activity.state}
        </span>
      ) : null}
      {isDeliveringResult ? (
        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800">
          Delivering result…
        </span>
      ) : null}
      {isRetrying ? (
        <Tooltip content={activity.lastFailureMessage ?? 'No failure message recorded'} placement="top">
          <span className="cursor-help rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
            Retry {activity.attempt}
            {activity.maximumAttempts ? `/${activity.maximumAttempts}` : ''}
          </span>
        </Tooltip>
      ) : null}
      {heartbeatStale ? (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
          No heartbeat for {Math.round((age as number) / 60000)}m
        </span>
      ) : null}
      <a
        href={debugUrl}
        target="_blank"
        rel="noreferrer"
        className="text-xs font-medium text-[var(--color-primary)] underline-offset-2 hover:underline"
      >
        Debug output
      </a>
    </li>
  );
};

type TProps = {
  workflows: TemporalWorkflowStatusDto[];
};

export const LiveExecutionPanel = ({ workflows }: TProps) => {
  const [expanded, setExpanded] = useState(false);
  const [temporalTarget, setTemporalTarget] = useState<TemporalTarget>(resolveFallbackTarget);

  useEffect(() => {
    let cancelled = false;
    getTemporalConfig()
      .then((cfg) => {
        if (!cancelled) {
          setTemporalTarget({ baseUrl: cfg.webUiUrl, namespace: cfg.namespace });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const statusSummary = useMemo(() => {
    const counts = workflows.reduce<Record<string, number>>((acc, workflow) => {
      acc[workflow.status] = (acc[workflow.status] ?? 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .map(([status, count]) => `${count} ${status}`)
      .join(' · ');
  }, [workflows]);

  if (!workflows.length) {
    return null;
  }

  return (
    <div className="rounded-lg border border-[var(--color-border)] p-4">
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={expanded}
      >
        <span>
          <span className="block text-sm font-semibold text-[var(--color-text-subtle)]">Temporal status</span>
          <span className="mt-1 block text-xs text-[var(--color-text-muted)]">{statusSummary}</span>
        </span>
        <span className="shrink-0 rounded-md border border-[var(--color-border)] px-2 py-1 text-xs font-medium text-[var(--color-text-subtle)]">
          {expanded ? 'Collapse' : 'Expand'}
        </span>
      </button>

      {expanded ? (
        <ul className="mt-3 space-y-3">
          {workflows.map((workflow) => {
            const workflowRunning = workflow.status === 'RUNNING';
            const debugUrl = getTemporalDebugUrl(workflow, temporalTarget);
            return (
              <li
                key={`${workflow.workflowId}-${workflow.runId}`}
                className="rounded-md border border-[var(--color-surface-muted)] p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold text-[var(--color-text)]">{formatWorkflowType(workflow.type)}</p>
                  <PipelineStatusTag status={workflow.status} className="!px-2 !py-0.5 text-xs font-semibold">
                    {workflow.status}
                  </PipelineStatusTag>
                  <span className="text-xs text-[var(--color-text-muted)]">{workflow.taskQueue}</span>
                  <a
                    href={debugUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto text-xs font-medium text-[var(--color-primary)] underline-offset-2 hover:underline"
                  >
                    Debug output
                  </a>
                </div>
                <p className="mt-1 break-all text-xs text-[var(--color-text-muted)]">{workflow.workflowId}</p>
                <p className="mt-1 text-xs text-[var(--color-text-muted)]">
                  Started {formatDateTime(workflow.startedAt)}
                  {workflow.closedAt ? ` • Closed ${formatDateTime(workflow.closedAt)}` : ''}
                </p>
                {workflow.pendingActivities.length ? (
                  <ul className="mt-2 space-y-1.5 border-t border-[var(--color-surface-muted)] pt-2">
                    {workflow.pendingActivities.map((activity, index) => (
                      <PendingActivityRow
                        key={`${activity.activityType ?? 'activity'}-${index}`}
                        activity={activity}
                        debugUrl={debugUrl}
                        workflowRunning={workflowRunning}
                      />
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};
